import { useEffect, useState } from 'react'
import { Alert, Button, Group, Loader, NumberInput, Stack, Text } from '@mantine/core'
import { ApiError } from '@/lib/api'
import { EXPORT_LIMIT_PRESETS, formatCount } from '@/lib/export-limits'
import type { ExportTicketsPreview } from '@/types/api'

/** Лимит строк: поле + быстрые варианты. null — выгрузить все обращения за период. */
export function ExportLimitField({
  value,
  onChange,
  disabled,
}: {
  value: number | null
  onChange: (next: number | null) => void
  disabled?: boolean
}) {
  return (
    <Stack gap={4}>
      <NumberInput
        label="Лимит строк"
        placeholder="Все обращения"
        min={1}
        allowDecimal={false}
        allowNegative={false}
        thousandSeparator=" "
        value={value ?? ''}
        disabled={disabled}
        onChange={(v) => onChange(typeof v === 'number' && v > 0 ? v : null)}
        w={260}
      />
      <Group gap={4}>
        {EXPORT_LIMIT_PRESETS.map((preset) => (
          <Button
            key={preset}
            size="compact-xs"
            variant={value === preset ? 'filled' : 'light'}
            disabled={disabled}
            onClick={() => onChange(preset)}
          >
            {formatCount(preset)}
          </Button>
        ))}
        <Button
          size="compact-xs"
          variant={value == null ? 'filled' : 'light'}
          disabled={disabled}
          onClick={() => onChange(null)}
        >
          Все
        </Button>
      </Group>
    </Stack>
  )
}

/**
 * Считает на сервере, сколько обращений попадёт в выгрузку, с небольшой задержкой после ввода.
 * load = null — параметры неполные, считать нечего.
 */
export function useExportPreview(load: (() => Promise<ExportTicketsPreview>) | null, deps: unknown[]) {
  const [preview, setPreview] = useState<ExportTicketsPreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (!load) {
      setPreview(null)
      setError(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    const timer = window.setTimeout(() => {
      load()
        .then((row) => { if (!cancelled) { setPreview(row); setError(null) } })
        .catch((e) => {
          if (cancelled) return
          setPreview(null)
          // 400 — неверный период; сеть/права не показываем, сервер проверит при выгрузке.
          setError(e instanceof ApiError && e.status === 400 ? e.message : null)
        })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, 400)
    return () => { cancelled = true; window.clearTimeout(timer) }
  }, deps)
  return { preview, error, loading }
}

export function ExportPreviewNote({
  preview,
  error,
  loading,
}: {
  preview: ExportTicketsPreview | null
  error: string | null
  loading: boolean
}) {
  if (loading && !preview) {
    return <Group gap={6}><Loader size="xs" /><Text size="sm" c="dimmed">Считаем обращения…</Text></Group>
  }
  if (error) return <Alert color="yellow" variant="light">{error}</Alert>
  if (!preview) return null
  const cut = preview.found - preview.will_export
  return (
    <Group gap={6}>
      {loading ? <Loader size="xs" /> : null}
      <Text size="sm">
        Найдено {formatCount(preview.found)} · в файл попадёт <b>{formatCount(preview.will_export)}</b>
      </Text>
      {cut > 0 ? (
        <Text size="sm" c="orange">
          — лимит отрежет {formatCount(cut)}, самые поздние по дате создания
        </Text>
      ) : null}
    </Group>
  )
}
